import React, { useEffect, useContext, useMemo } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useErrorHandler } from 'react-error-boundary';
import styled from 'styled-components';
import { AxiosError } from 'axios';
import { intersectionBy, differenceBy } from 'lodash';

import Headline2 from '@joinforma/ui/components/Headline2';
import SvgIcon from '@joinforma/ui/components/SvgIcon';
import Title2 from '@joinforma/ui/components/Title2';
import TabBar from '@joinforma/ui/components/TabBar';
import TabItem from '@joinforma/ui/components/TabItem';
import { ReactComponent as PlusCircleIcon } from '@joinforma/ui/assets/icons/plus-circle.svg';
import { SvgIconColor, SvgIconSize } from '@joinforma/ui/constants/properties';

import useQueryStripeCards from 'hooks/useQueryStripeCards';

import { EVENTS } from 'constants/events';
import { FeatureFlag } from 'constants/featureFlag';
import { CardStatus } from 'constants/stripe';

import LinkedAccounts from './LinkedAccounts';

import { SettingsRoutes } from 'utils/routeGenerator';
import { logEvent } from 'utils/amplitude';
import { recordSprigPosttaxCardAttributes } from 'utils/sprig';

import CurrentUserContext from 'contexts/CurrentUserContext';
import FeatureFlagContext from 'contexts/FeatureFlagContext';
import PretaxContext from 'contexts/PretaxContext';

import { Employee, PosttaxCard } from './types';

const Container = styled.div``;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const AddCardLink = styled(NavLink)`
  outline: none;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px 10px;
  cursor: pointer;
`;

const AddIcon = styled(SvgIcon).attrs({
  icon: PlusCircleIcon,
  size: SvgIconSize.MEDIUM,
  fill: SvgIconColor.INTERACTIVE,
})`
  margin-right: 5px;
`;

const AddCardLinkText = styled(Title2)`
  color: ${({ theme }) => theme.colors.text.link};
`;

const Title = styled(Headline2)``;

const EligibleAccountsContainer = styled.div``;

const StyledTabBar = styled(TabBar)``;

const Main = styled.div`
  padding-top: 64px;
`;

const CardsLayout = ({ children }: { children?: React.ReactNode }) => {
  const location = useLocation();
  const { employee }: { employee: Employee } = useContext(CurrentUserContext);
  const { pretax_accounts } = useContext(PretaxContext);
  const employeeWallets = employee.employee_wallets;

  const featureFlag = useContext(FeatureFlagContext);

  const sprigUserResearchEnabled = featureFlag.flag(
    FeatureFlag.SPRIG_USER_RESEARCH,
    false
  );

  const handleError = useErrorHandler();
  const { data: cards_data, isLoading: is_cards_loading } = useQueryStripeCards(
    {
      onError: (error: AxiosError) => handleError(error),
      onSuccess: sprigUserResearchEnabled
        ? ({ cards }) => recordSprigPosttaxCardAttributes(cards)
        : undefined,
    }
  );

  const isSettingsActive = useMemo(
    () => location.pathname.includes('/cards/settings'),
    [location.pathname]
  );

  useEffect(() => {
    logEvent(EVENTS.TwicCardViewed, {});
  }, []);

  const renderButton = (cards: Array<PosttaxCard>) => {
    const active_cards = cards.filter(
      ({ status }) => status !== CardStatus.CANCELED
    );
    const has_physical_card = active_cards.some(
      ({ type }) => type === 'physical'
    );
    const has_virtual_card = active_cards.some(
      ({ type }) => type === 'virtual'
    );

    const has_at_least_one_card = has_physical_card || has_virtual_card;
    const has_both_cards = has_physical_card && has_virtual_card;

    if (has_at_least_one_card && !has_both_cards) {
      return (
        <AddCardLink
          to={SettingsRoutes.posttaxCardNew({
            card_type: has_physical_card ? 'virtual' : 'physical',
          })}
        >
          <AddIcon />
          <AddCardLinkText>
            Add {has_physical_card ? 'Virtual' : 'Debit'} Card
          </AddCardLinkText>
        </AddCardLink>
      );
    }

    return null;
  };

  const renderCardSettingsLink = (employee: Employee) => {
    const {
      settings: { country, stripe_cardholder_exists },
    } = employee;

    if (country === 'us' && stripe_cardholder_exists) {
      return (
        <TabItem
          active={isSettingsActive}
          to={SettingsRoutes.posttaxCardEdit()}
        >
          Card settings
        </TabItem>
      );
    }

    return null;
  };

  const pretaxWallets = intersectionBy(employeeWallets, pretax_accounts, 'id');

  const postTaxWallets = useMemo(
    () =>
      differenceBy(employeeWallets, pretaxWallets, 'id').filter(
        ({
          visible_to_employee,
          company_wallet_configuration: { is_twic_card_payment_allowed },
        }) => visible_to_employee && is_twic_card_payment_allowed
      ),
    [employeeWallets, pretaxWallets]
  );

  return (
    <Container>
      <TitleContainer>
        <Title>Forma Card</Title>
        {!is_cards_loading && renderButton(cards_data!.cards)}
      </TitleContainer>
      <EligibleAccountsContainer>
        <LinkedAccounts employee_wallets={postTaxWallets} />
      </EligibleAccountsContainer>
      <StyledTabBar>
        <TabItem
          active={!isSettingsActive}
          to={SettingsRoutes.posttaxCardList()}
        >
          Manage cards
        </TabItem>
        {renderCardSettingsLink(employee)}
      </StyledTabBar>
      <Main>{children ?? <Outlet />}</Main>
    </Container>
  );
};

export default CardsLayout;
